// src/components/ShareDialogContent.tsx
/**
 * T-35: Share Dialog Content Component
 * Content component for the new UI framework Share Dialog
 * Extracted from the existing ShareDialog component
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { setShareLoading, setLastShareCode, setShareUrl } from '../store/slices/shareSlice';
import { shareScene } from '../lib/shareApi';

interface ShareResult {
  id: string;
  url: string;
  metadata: {
    id: string;
    size_bytes: number;
    schema_version: number;
    created_at: string;
  };
}

export const ShareDialogContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(state => state.share.isLoading);
  const state = useAppSelector(state => state);
  
  const [shareResult, setShareResult] = useState<ShareResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Reset state when component mounts
  useEffect(() => {
    setShareResult(null);
    setError(null);
    setCopySuccess(false);
  }, []);

  const handleShare = useCallback(async () => {
    setError(null);
    dispatch(setShareLoading(true));
    
    try {
      console.log('Starting share process...');
      const result = await shareScene(state);
      
      setShareResult(result);
      dispatch(setLastShareCode(result.id));
      dispatch(setShareUrl(result.url));
      
      console.log('Share successful:', result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to share scene';
      console.error('Share failed:', err);
      setError(errorMessage);
    } finally {
      dispatch(setShareLoading(false));
    }
  }, [state, dispatch]);

  const handleCopyUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      setError('Failed to copy URL to clipboard');
    }
  }, []);

  const handleCopyId = useCallback(async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy ID:', err);
      setError('Failed to copy scene ID to clipboard');
    }
  }, []);

  return (
    <div className="space-y-4">
      {!shareResult && !isLoading && (
        <div>
          <p className="text-gray-600 mb-6">
            Create a shareable link for your number line scene. Others can use this link to load your exact configuration.
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleShare}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Generate Share Link
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Preparing scene for sharing...</p>
          <p className="text-sm text-gray-500 mt-2">Serializing and uploading data</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-800 text-sm">{error}</span>
          </div>
        </div>
      )}

      {shareResult && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-green-800 font-medium">Scene shared successfully!</span>
            </div>
          </div>

          {/* Share URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Share URL
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={shareResult.url}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
              />
              <button
                onClick={() => handleCopyUrl(shareResult.url)}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Scene ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scene ID
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={shareResult.id}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
              />
              <button
                onClick={() => handleCopyId(shareResult.id)}
                className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                Copy ID
              </button>
            </div>
          </div>

          {/* Metadata */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Scene Details</h4>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
              <div>
                <span className="font-medium">Size:</span> {(shareResult.metadata.size_bytes / 1024).toFixed(1)}KB
              </div>
              <div>
                <span className="font-medium">Version:</span> {shareResult.metadata.schema_version}
              </div>
              <div className="col-span-2">
                <span className="font-medium">Created:</span> {new Date(shareResult.metadata.created_at).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>How to use:</strong> Share the URL with others. They can paste it into their browser 
              or use the "Load Shared Scene" feature to access your number line configuration.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setShareResult(null)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Share Another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 